import { useState } from "react";
import { Link, Redirect } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  customerVehiclesQueryKey,
  useAddCustomerVehicle,
  useDeleteCustomerVehicle,
  useGetCustomerVehicles,
  useUpdateCustomerVehicle,
} from "@orbit/api-client-react";
import type { CustomerVehicleListResponse, VehicleSize } from "@orbit/api-zod";
import { useAuth } from "../hooks/useAuth";
import { Wordmark } from "../components/Brand";
import { ConfirmDestructiveAction } from "../components/ConfirmDestructiveAction";

type Vehicle = CustomerVehicleListResponse["vehicles"][number];

/**
 * /profile/vehicles — Phase 26 plate registration.
 *
 * Customers can register up to 5 plates. Each row shows the canonical
 * uppercased plate, an optional nickname, and (for the future
 * CarWash OS UX) an optional vehicle size. Add/edit lives in inline
 * forms (no modal navigation away from the page); delete is gated by
 * the shared ConfirmDestructiveAction component so a swipe can't
 * accidentally drop a row.
 *
 * Plates here are display/personalization metadata only — no tenant
 * lookup, no sync to any OS product. See PHASE_26 spec + DECISIONS
 * D92 (data-export extension).
 */
export function ProfileVehiclesPage() {
  const { isAuthenticated } = useAuth();
  const qc = useQueryClient();
  const listQ = useGetCustomerVehicles({ enabled: isAuthenticated });

  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [deleting, setDeleting] = useState<Vehicle | null>(null);

  const deleteMut = useDeleteCustomerVehicle();

  if (!isAuthenticated) return <Redirect to="/" />;

  const vehicles = listQ.data?.vehicles ?? [];
  const count = listQ.data?.count ?? 0;
  const max = listQ.data?.max ?? 5;
  const remaining = Math.max(0, max - count);
  const atCap = count >= max;

  async function refresh() {
    await qc.invalidateQueries({ queryKey: [...customerVehiclesQueryKey] });
  }

  async function onConfirmDelete() {
    if (!deleting) return;
    try {
      await deleteMut.mutateAsync({ vehicleId: deleting.id });
      await refresh();
      setDeleting(null);
    } catch (err) {
      // ConfirmDestructiveAction surfaces the apiError.message.
      throw err;
    }
  }

  return (
    <div className="min-h-screen pb-16">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link href="/profile" className="text-xs text-ink-dark underline">
            Back
          </Link>
        </header>

        <h1 className="mt-6 text-xl font-bold text-navy">Vehicles</h1>
        <p className="mt-1 text-sm text-ink-dark">
          Register up to {max} plates so car-service merchants — car
          washes, repair shops, fuel stations, parking — can recognize
          your vehicle and credit your visit faster.
        </p>

        <p
          className="mt-3 text-[11px] uppercase tracking-wider text-ink-dark/60"
          data-testid="text-vehicles-counter"
        >
          {count}/{max} registered. {remaining} remaining.
        </p>

        {/* List */}
        <section className="mt-4 space-y-3">
          {listQ.isLoading ? (
            <div className="h-16 animate-pulse rounded-xl bg-navy/5" />
          ) : listQ.error ? (
            <p className="text-sm text-red-600">Couldn't load your vehicles.</p>
          ) : vehicles.length === 0 ? (
            <div
              className="card text-center text-sm text-ink-dark"
              data-testid="text-vehicles-empty"
            >
              No vehicles yet. Add your first plate below.
            </div>
          ) : (
            vehicles.map((v) => (
              <VehicleRow
                key={v.id}
                vehicle={v}
                onEdit={() => setEditing(v)}
                onDelete={() => setDeleting(v)}
              />
            ))
          )}
        </section>

        {/* Add CTA */}
        <div className="mt-4">
          {atCap ? (
            <button
              type="button"
              className="btn-secondary w-full"
              disabled
              title={`Maximum of ${max} vehicles reached. Delete one to add another.`}
              data-testid="button-add-vehicle-disabled"
            >
              At {max}-vehicle limit
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => setAddOpen(true)}
              data-testid="button-add-vehicle"
            >
              + Add vehicle
            </button>
          )}
        </div>
      </div>

      {addOpen && (
        <VehicleFormDialog
          mode="add"
          onClose={() => setAddOpen(false)}
          onSaved={async () => {
            await refresh();
            setAddOpen(false);
          }}
        />
      )}

      {editing && (
        <VehicleFormDialog
          mode="edit"
          vehicle={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await refresh();
            setEditing(null);
          }}
        />
      )}

      <ConfirmDestructiveAction
        open={Boolean(deleting)}
        title="Remove this vehicle?"
        body={
          deleting ? (
            <>
              <strong>{deleting.plateNumber}</strong>
              {deleting.nickname ? ` (${deleting.nickname})` : ""} will be
              removed from your profile. You can re-add it later.
            </>
          ) : null
        }
        confirmLabel="Remove vehicle"
        onCancel={() => setDeleting(null)}
        onConfirm={onConfirmDelete}
        busy={deleteMut.isPending}
      />
    </div>
  );
}

function VehicleRow(props: {
  vehicle: Vehicle;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { vehicle, onEdit, onDelete } = props;
  return (
    <div
      className="card flex items-start justify-between gap-3"
      data-testid={`vehicle-row-${vehicle.id}`}
    >
      <div className="min-w-0">
        <p className="font-mono text-base font-semibold text-navy">
          {vehicle.plateNumber}
        </p>
        {vehicle.nickname && (
          <p className="mt-0.5 text-sm text-ink-dark">{vehicle.nickname}</p>
        )}
        {vehicle.vehicleSize && (
          <p className="mt-0.5 text-[11px] uppercase tracking-wider text-ink-dark/60">
            {sizeLabel(vehicle.vehicleSize)}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <button
          type="button"
          onClick={onEdit}
          className="text-xs text-navy underline"
          data-testid={`button-edit-vehicle-${vehicle.id}`}
        >
          Edit
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="text-xs text-red-700 underline"
          data-testid={`button-delete-vehicle-${vehicle.id}`}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function sizeLabel(size: VehicleSize): string {
  switch (size) {
    case "SMALL":
      return "Small";
    case "MEDIUM":
      return "Medium";
    case "LARGE":
      return "Large";
    case "EXTRA_LARGE":
      return "Extra Large";
  }
}

/**
 * Bottom-sheet style form. Re-used for add + edit. In edit mode the
 * plate field is read-only — a customer who wants to change a plate
 * must remove and re-add (matches the storage layer which deliberately
 * doesn't accept plateNumber on the PATCH route).
 */
function VehicleFormDialog(props: {
  mode: "add" | "edit";
  vehicle?: Vehicle;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { mode, vehicle, onClose, onSaved } = props;
  const addMut = useAddCustomerVehicle();
  const updateMut = useUpdateCustomerVehicle();

  const [plate, setPlate] = useState(vehicle?.plateNumber ?? "");
  const [nickname, setNickname] = useState(vehicle?.nickname ?? "");
  const [size, setSize] = useState<VehicleSize | "">(vehicle?.vehicleSize ?? "");
  // Migration 032 — optional brand + model. Free-text so customers
  // can enter any make/model (Toyota Hilux, Honda Civic, Mitsubishi
  // Pajero, Isuzu MU-X, …). Trimmed/empty-coerced server-side.
  const [brand, setBrand] = useState(vehicle?.brand ?? "");
  const [model, setModel] = useState(vehicle?.model ?? "");
  const [error, setError] = useState<string | null>(null);

  const busy = addMut.isPending || updateMut.isPending;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (mode === "add") {
        await addMut.mutateAsync({
          plateNumber: plate,
          nickname: nickname.trim() ? nickname.trim() : null,
          vehicleSize: size === "" ? null : size,
          // Migration 032 — empty string → null via the server-side
          // zod transform; mirroring it here keeps wire payloads tidy.
          brand: brand.trim() ? brand.trim() : null,
          model: model.trim() ? model.trim() : null,
        });
      } else if (vehicle) {
        await updateMut.mutateAsync({
          vehicleId: vehicle.id,
          body: {
            nickname: nickname.trim() ? nickname.trim() : null,
            vehicleSize: size === "" ? null : size,
            brand: brand.trim() ? brand.trim() : null,
            model: model.trim() ? model.trim() : null,
          },
        });
      }
      await onSaved();
    } catch (err) {
      const e = err as { apiError?: { message?: string } };
      setError(e.apiError?.message ?? "That didn't work. Try again.");
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-3"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vf-title"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md card bg-white rounded-t-2xl sm:rounded-2xl shadow-xl"
      >
        <h2 id="vf-title" className="text-base font-semibold text-navy">
          {mode === "add" ? "Add vehicle" : "Edit vehicle"}
        </h2>

        <label className="block mt-3">
          <span className="text-xs uppercase tracking-wider text-ink-dark/70">
            Plate number
          </span>
          <input
            className="input mt-1 font-mono uppercase"
            value={plate}
            onChange={(e) =>
              // Live-uppercase as the user types so what they see is what
              // gets stored. The Zod transform on the wire is the canonical
              // source of truth, but mirroring it here removes any "wait,
              // why did my plate get uppercased?" surprise.
              setPlate(e.target.value.toUpperCase().slice(0, 12))
            }
            placeholder="ABC 1234"
            maxLength={12}
            disabled={mode === "edit"}
            required
            data-testid="input-plate-number"
          />
          <span className="text-[11px] text-ink-dark/70">
            2-12 characters. Letters, digits, hyphens, and spaces.
            {mode === "edit" && " To change a plate, remove and re-add it."}
          </span>
        </label>

        <label className="block mt-3">
          <span className="text-xs uppercase tracking-wider text-ink-dark/70">
            Nickname (optional)
          </span>
          <input
            className="input mt-1"
            value={nickname}
            onChange={(e) => setNickname(e.target.value.slice(0, 50))}
            placeholder="Daily driver"
            maxLength={50}
            data-testid="input-vehicle-nickname"
          />
        </label>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-ink-dark/70">
              Brand (optional)
            </span>
            <input
              className="input mt-1"
              value={brand}
              onChange={(e) => setBrand(e.target.value.slice(0, 40))}
              placeholder="Toyota"
              maxLength={40}
              data-testid="input-vehicle-brand"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-ink-dark/70">
              Model (optional)
            </span>
            <input
              className="input mt-1"
              value={model}
              onChange={(e) => setModel(e.target.value.slice(0, 60))}
              placeholder="Hilux"
              maxLength={60}
              data-testid="input-vehicle-model"
            />
          </label>
        </div>

        <label className="block mt-3">
          <span className="text-xs uppercase tracking-wider text-ink-dark/70">
            Size (optional)
          </span>
          <select
            className="input mt-1"
            value={size}
            onChange={(e) => setSize(e.target.value as VehicleSize | "")}
            data-testid="select-vehicle-size"
          >
            <option value="">— not specified —</option>
            <option value="SMALL">Small</option>
            <option value="MEDIUM">Medium</option>
            <option value="LARGE">Large</option>
            <option value="EXTRA_LARGE">Extra Large</option>
          </select>
        </label>

        {error && (
          <p
            className="mt-3 text-sm text-red-600"
            role="alert"
            data-testid="text-vehicle-form-error"
          >
            {error}
          </p>
        )}

        <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2">
          <button
            type="button"
            className="btn-secondary flex-1"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary flex-1"
            disabled={busy}
            data-testid="button-save-vehicle"
          >
            {busy ? "Saving…" : mode === "add" ? "Add vehicle" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
